import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import {
  Upload, Download, FileSpreadsheet, Loader2, CheckCircle, AlertTriangle, X,
} from 'lucide-react';

/**
 * Shared CSV bulk-merchant import dialog. Drop into:
 *   • AdminMerchantsPage   → urlBase="/api/admin/merchants/import"
 *   • MRODashboard          → urlBase="/api/mro/merchants/import"
 *
 * Props:
 *   api          axios instance from useAuth()
 *   urlBase      base API path
 *   onImported   () => void, called after a successful (non-dry-run) import
 *   testIdPrefix string prefix for data-testids
 *
 * Trigger renders as a button so callers don't need to manage open state.
 */
export function MerchantImportDialog({ api, urlBase, onImported, testIdPrefix = 'import' }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const reset = () => {
    setFile(null);
    setResult(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const onClose = (next) => {
    setOpen(next);
    if (!next) reset();
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get(`${urlBase}/template`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'merchant_import_template.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (e) {
      toast.error('Failed to download template');
    }
  };

  const handlePick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.csv')) {
      toast.error('Only .csv files are supported');
      return;
    }
    setFile(f);
    setResult(null);
  };

  const send = async (dryRun) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post(`${urlBase}?dry_run=${dryRun}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(data);
      if (dryRun) {
        toast.message(`Preview ready: ${data.created.length} new · ${data.skipped.length} skipped · ${data.errors.length} errors`);
      } else {
        toast.success(`${data.created.length} merchant(s) imported (status: pending)`);
        onImported?.();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    }
    setBusy(false);
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        variant="outline"
        className="border-white/20 text-[#A0B4CC] hover:bg-white/5"
        data-testid={`${testIdPrefix}-open-btn`}
      >
        <Upload className="w-4 h-4 mr-1.5" /> Import CSV
      </Button>

      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="bg-[#041A3B] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-[#00E676]" />
              Bulk Import Merchants
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Instructions */}
            <div className="p-3 rounded-lg bg-[#000B18] border border-white/5 text-xs text-[#A0B4CC] leading-relaxed">
              <p>
                Upload a CSV with the columns: <code className="text-[#00E5FF]">name, email, business_name, business_address, industry</code> (required) and <code className="text-[#5C7599]">mobile, business_description, business_type</code> (optional).
              </p>
              <p className="mt-1">
                Imported merchants land in the <b className="text-amber-400">pending</b> queue for review/approval. Each receives a temporary opaque password — they activate via the <b>Forgot password</b> flow on first login.
              </p>
            </div>

            <div className="flex gap-2 flex-wrap">
              <Button
                size="sm"
                onClick={downloadTemplate}
                variant="outline"
                className="border-white/20 text-[#A0B4CC]"
                data-testid={`${testIdPrefix}-template-btn`}
              >
                <Download className="w-3.5 h-3.5 mr-1.5" /> Download template
              </Button>
              <input
                ref={inputRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={handlePick}
                data-testid={`${testIdPrefix}-file-input`}
              />
              <Button
                size="sm"
                onClick={() => inputRef.current?.click()}
                className="bg-[#00E5FF] text-[#000B18] hover:bg-[#00E5FF]/90"
                data-testid={`${testIdPrefix}-pick-btn`}
              >
                <Upload className="w-3.5 h-3.5 mr-1.5" /> Choose CSV file
              </Button>
              {file && (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-[#000B18] border border-white/10 text-xs">
                  <FileSpreadsheet className="w-3.5 h-3.5 text-[#00E5FF]" />
                  <span className="text-white">{file.name}</span>
                  <span className="text-[#5C7599]">({Math.round(file.size / 1024)} KB)</span>
                  <button onClick={reset} className="text-[#FF1744] hover:bg-[#FF1744]/10 rounded p-0.5" aria-label="Remove file">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}
            </div>

            {/* Preview / Result */}
            {result && (
              <div className="space-y-3" data-testid={`${testIdPrefix}-result`}>
                <div className="grid grid-cols-3 gap-2">
                  <Stat label="Created" value={result.created.length} color="text-[#00E676]" />
                  <Stat label="Skipped" value={result.skipped.length} color="text-amber-400" />
                  <Stat label="Errors" value={result.errors.length} color="text-[#FF1744]" />
                </div>
                {result.dry_run && (
                  <p className="text-[10px] text-amber-400 text-center">
                    🔎 PREVIEW MODE — nothing has been saved yet. Click <b>Import</b> below to actually create these merchants.
                  </p>
                )}

                {result.created.length > 0 && (
                  <ResultList
                    title="Will be created"
                    items={result.created}
                    color="text-[#00E676]"
                    icon={<CheckCircle className="w-3.5 h-3.5 text-[#00E676]" />}
                    fmt={(c) => `Row ${c.row} — ${c.email} (${c.business_name}${c.warning ? ` ⚠ ${c.warning}` : ''})`}
                  />
                )}
                {result.skipped.length > 0 && (
                  <ResultList
                    title="Skipped"
                    items={result.skipped}
                    color="text-amber-400"
                    icon={<AlertTriangle className="w-3.5 h-3.5 text-amber-400" />}
                    fmt={(s) => `Row ${s.row} — ${s.email} (${s.reason})`}
                  />
                )}
                {result.errors.length > 0 && (
                  <ResultList
                    title="Errors"
                    items={result.errors}
                    color="text-[#FF1744]"
                    icon={<X className="w-3.5 h-3.5 text-[#FF1744]" />}
                    fmt={(e) => `Row ${e.row} — ${e.email || '(no email)'} (${e.reason})`}
                  />
                )}
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => onClose(false)} className="text-[#A0B4CC]">
              Close
            </Button>
            {file && !result?.dry_run && (
              <Button
                onClick={() => send(true)}
                disabled={busy}
                variant="outline"
                className="border-white/20 text-[#A0B4CC]"
                data-testid={`${testIdPrefix}-preview-btn`}
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Preview'}
              </Button>
            )}
            {file && (
              <Button
                onClick={() => send(false)}
                disabled={busy}
                className="bg-[#00E676] text-[#000B18] hover:bg-[#00E676]/90"
                data-testid={`${testIdPrefix}-submit-btn`}
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-1.5" /> Import</>}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="p-2 rounded bg-[#000B18] border border-white/10 text-center">
      <p className={`font-outfit text-xl font-bold ${color}`}>{value}</p>
      <p className="text-[10px] text-[#5C7599] uppercase tracking-wide">{label}</p>
    </div>
  );
}

function ResultList({ title, items, color, icon, fmt }) {
  return (
    <div className="p-3 rounded-lg bg-[#000B18] border border-white/5">
      <p className={`text-xs font-medium uppercase tracking-wide mb-2 ${color}`}>{title} ({items.length})</p>
      <div className="space-y-1 max-h-40 overflow-y-auto pr-1">
        {items.map((it, i) => (
          <div key={`${title}-${i}-${String(fmt(it)).slice(0, 32)}`} className="text-xs text-[#A0B4CC] flex items-start gap-1.5">
            <span className="mt-0.5">{icon}</span>
            <span className="break-words">{fmt(it)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
